import {useEffect, useState} from "react";
import {Dialog, DialogActions, DialogContent, Tab, Tabs} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import MuseumIcon from "@mui/icons-material/Museum";
import PublicIcon from "@mui/icons-material/Public";
import HistoryIcon from "@mui/icons-material/History";
import {useDocumentTranslation} from "../documents/documentContext";
import {SharePrivatePanel} from "./private/SharePrivatePanel";
import {SharePublicPanel} from "./public/SharePublicPanel";
import {ShareObjectHistoryPanel} from "./ShareObjectHistoryPanel";
import {RotateDeviceWarningOverlay} from "../app/RotateDeviceWarningOverlay";

/**
 * Unified document sharing dialog with three tabs:
 *  0 – "Del internt"    Share with named DAMS users via email link
 *  1 – "Del offentlig"  Share via a time-limited portal link + email
 *  2 – "Historikk"      View, deactivate and renew existing public shares
 *
 * @param {boolean}  open              - Whether the dialog is open.
 * @param {Object}   model             - The document model.
 * @param {Function} closeCallback     - Called when the dialog should close.
 * @param {number}   [initialTab=0]    - Tab to open on (0=private, 1=public, 2=history).
 */
export const ModelSharingDialog = ({open, model, closeCallback, initialTab = 0}) => {
    const t = useDocumentTranslation();

    const [activeTab, setActiveTab] = useState(initialTab);
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

    useEffect(() => {
        if (open) setActiveTab(initialTab);
    }, [open, initialTab]);

    const _handleShareSuccess = () => {
        setHistoryRefreshKey(k => k + 1);
    };

    return <>
        <Dialog
            open={!!open}
            onClose={closeCallback}
            slotProps={{paper: {sx: {minWidth: '80%', minHeight: '80%'}}}}
        >
            <DialogTitle>
                {t('modelSharingDialogTitle', 'Del dokument')} – {model?.title}
            </DialogTitle>

            <DialogContent sx={{pt: 0, display: 'flex', flexDirection: 'column'}}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}
                >
                    <Tab
                        icon={<MuseumIcon fontSize="small"/>}
                        label={t('tabSharePrivate', 'Del internt')}
                        iconPosition="start"
                    />
                    <Tab
                        icon={<PublicIcon fontSize="small"/>}
                        label={t('tabSharePublic', 'Del offentlig')}
                        iconPosition="start"
                    />
                    <Tab
                        icon={<HistoryIcon fontSize="small"/>}
                        label={t('tabShareHistory', 'Historikk')}
                        iconPosition="start"
                    />
                </Tabs>

                <Box hidden={activeTab !== 0}>
                    <SharePrivatePanel
                        collectionId={model?.collectionId}
                        modelId={model?.uniqueId}
                        onShareSuccess={_handleShareSuccess}
                    />
                </Box>
                <Box hidden={activeTab !== 1}>
                    <SharePublicPanel
                        model={model}
                        onShareSuccess={_handleShareSuccess}
                    />
                </Box>
                <Box sx={{flex: 1, overflow: 'auto'}} hidden={activeTab !== 2}>
                    <ShareObjectHistoryPanel
                        model={model}
                        open={open}
                        refreshKey={historyRefreshKey}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={closeCallback}>{t('btnClose', 'Lukk')}</Button>
            </DialogActions>
        </Dialog>

        {open && <RotateDeviceWarningOverlay/>}
    </>;
};

